import { FC, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { toast } from 'react-toastify';
import { ClipboardCopy, ClipboardPaste } from 'lucide-react';
import api from '@/api/api';

interface CopyPasteComponentProps {
  fileId: string;
  pageNumber: number;
  refetch: () => Promise<void>;
  clearCanvas: () => void;
  isDisabled: boolean;
}

const CopyPasteComponent: FC<CopyPasteComponentProps> = ({ fileId, pageNumber, refetch, clearCanvas, isDisabled }) => {
  const [pasteDialogVisible, setPasteDialogVisible] = useState(false);
  const [withData, setWithData] = useState(false);
  const [pasteData, setPasteData] = useState<{ source_doc_uid: string; source_page_number: number } | null>(null);

  const copyPageInfo = () => {
    const pageInfo = JSON.stringify({ source_doc_uid: fileId, source_page_number: pageNumber - 1 });
    navigator.clipboard.writeText(pageInfo);
    toast.success('Data úspěšně zkopírována.');
  };

  const openPasteDialog = async () => {
    const clipboardContent = await navigator.clipboard.readText();
    try {
      const parsedData = JSON.parse(clipboardContent);
      if (parsedData.source_doc_uid && typeof parsedData.source_page_number === 'number') {
        setPasteData(parsedData);
        setPasteDialogVisible(true);
      } else {
        toast.error('Špatná data ze schránky.');
      }
    } catch (error) {
      console.error('Failed to parse clipboard data:', error);
      toast.error('Chyba při zpracování dat ze schránky.');
    }
  };

  const handlePasteData = async () => {
    if (!pasteData) return;

    try {
      const response = await api.patch('/copy-page', {
        source_doc_uid: pasteData.source_doc_uid,
        source_page_number: pasteData.source_page_number,
        target_doc_uid: fileId,
        target_page_number: pageNumber - 1,
        with_graphics: withData,
      });

      if (response.status === 200 || response.status === 202) {
        toast.success('Data byly úspěšny nakopírovány.');
        setPasteDialogVisible(false);
        setPasteData(null);
        clearCanvas();
        await refetch();
      }
    } catch (error) {
      console.error('Failed to copy page:', error);
      toast.error('Chyba při kopírování dat na stránku.');
    }
  };

  return (
    <>
      <Button
        icon={<ClipboardCopy size={22} />}
        text={true}
        severity={'secondary'}
        onClick={copyPageInfo}
        tooltip={'Zkopírovat stránku'}
        tooltipOptions={{ position: 'top' }}
        className={'text-sm focus:shadow-none'}
        disabled={isDisabled}
      />
      <Button
        icon={<ClipboardPaste size={22} />}
        text={true}
        severity={'info'}
        onClick={openPasteDialog}
        tooltip={'Vložit data'}
        tooltipOptions={{ position: 'top' }}
        className={'text-sm focus:shadow-none'}
        disabled={isDisabled}
      />

      <Dialog header="Vložit data na stránku" visible={pasteDialogVisible} onHide={() => setPasteDialogVisible(false)}>
        <div className="flex flex-col gap-4 mb-5">
          <div className="flex gap-2 items-center">
            <label htmlFor="withData">Včetně grafiky?</label>
            <Checkbox
              inputId="withData"
              checked={withData}
              onChange={(e: CheckboxChangeEvent) => setWithData(e.checked ?? false)}
            />
          </div>

          <div className={'flex items-center justify-between'}>
            <Button label="Zavřít" onClick={() => setPasteDialogVisible(false)} className={'btn-cancel'} />
            <Button label="Vložit" onClick={handlePasteData} autoFocus={true} className={'btn-secondary'} />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CopyPasteComponent;
